import { Box, Container, Paper, Typography } from '@mui/material';
import React from 'react';
import LayoutErrorBoundary from './LayoutErrorBoundary';

interface PageLayoutProps {
    children: React.ReactNode;
    title?: string;
    guidance?: React.ReactNode;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    actions?: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({ children, title, guidance, maxWidth = 'xl', actions }) => {
    return (
        <LayoutErrorBoundary>
            <Container
                maxWidth={maxWidth}
                disableGutters
                sx={{
                    width: '100%',
                    height: '100%',
                    px: { xs: 0, sm: 2, md: 3 },
                    backgroundColor: 'transparent', // Use transparent to inherit from parent
                    margin: 0,
                    maxWidth: '100%',
                    pt: 0, // FlowLayout already handles header spacing
                }}
            >
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    boxShadow: 'none',
                    overflow: 'visible',
                    bgcolor: 'transparent', // Use transparent to inherit from parent
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: { xs: 1.5, sm: 2.5 },
                        width: '100%',
                        py: 0,
                        my: 0,
                    }}
                >
                    {guidance ? (
                        <Box
                            sx={{
                                width: '100%',
                                backgroundColor: (theme) => 
                                    theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.02)'
                                        : 'rgba(0, 0, 0, 0.02)',
                                border: (theme) => 
                                    `1px solid ${theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.1)' 
                                        : 'rgba(0, 0, 0, 0.08)'}`,
                                borderRadius: 2,
                                p: 3,
                                mb: 3,
                                mt: { xs: '16px', sm: '8px' },
                                fontSize: {
                                    xs: '1rem',
                                    sm: '1.125rem',
                                    md: '1.25rem',
                                },
                                fontWeight: 450,
                                color: (theme) => theme.palette.text.primary,
                                '& svg': {
                                    fontSize: {
                                        xs: '1.25rem',
                                        sm: '1.5rem',
                                    },
                                },
                            }}
                        >
                            {guidance}
                        </Box>
                    ) : title ? (
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: {
                                    xs: '1.25rem',
                                    sm: '1.5rem',
                                    md: '1.75rem',
                                },
                                fontWeight: 500,
                                lineHeight: 1.4,
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                                mb: 0,
                                pb: '22px',
                                mt: { xs: '16px', sm: '8px' },
                            }}
                        >
                            {title}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        {actions}
                    </Box>
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        py: 0,
                        mt: '2px',
                        pb: { xs: '80px', sm: '16px' }, // Extra bottom padding on mobile for bottom navigation
                    }}
                >
                    {children}
                </Box>
            </Paper>
        </Container>
        </LayoutErrorBoundary>
    );
};

export default PageLayout;
